<template>
  <div class="login-container">
    <div class="login-form-container">
      <!-- Theme Toggle -->
      <button class="theme-toggle-btn" @click="themeStore.toggleDarkMode()" :title="themeStore.isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'">
        <span>{{ themeStore.isDark ? '☀️' : '🌙' }}</span>
      </button>

      <!-- Logo Section -->
      <div class="logo-section">
        <div class="logo-wrapper">
          <img src="@/assets/logo.png" alt="QUALI-D" class="logo" />
        </div>
        <div class="meta-partner-badge">
          <img
            :src="themeStore.isDark
              ? 'https://quali-d.com/resources/Meta_Business_Partners_inline_lockup_negative_primary_RGB.png'
              : 'https://quali-d.com/resources/Meta_Business_Partners_inline_lockup_positive_primary_RGB.png'"
            alt="Meta Business Partner"
            class="meta-partner-logo"
          />
        </div>
      </div>

      <!-- TOTP Step -->
      <div v-if="totpStep" class="form-section">
        <div class="form-header">
          <h2 class="form-title">Two-Factor Auth</h2>
          <p class="form-subtitle">Open your authenticator app and enter the 6-digit code</p>
        </div>

        <div class="totp-form">
          <div v-if="!useRecovery" class="form-group">
            <label class="form-label">Authenticator Code</label>
            <div class="input-wrapper">
              <svg class="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              <input
                v-model="totpCode"
                type="text"
                inputmode="numeric"
                maxlength="6"
                placeholder="000000"
                class="form-input totp-input"
                autocomplete="one-time-code"
                @keyup.enter="handleTotpVerify"
              />
            </div>
          </div>

          <div v-else class="form-group">
            <label class="form-label">Recovery Code</label>
            <div class="input-wrapper">
              <svg class="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              <input
                v-model="recoveryCode"
                type="text"
                placeholder="XXXXX-XXXXX"
                class="form-input"
                @keyup.enter="handleTotpVerify"
              />
            </div>
          </div>

          <div v-if="totpError" class="login-error">{{ totpError }}</div>

          <button
            type="button"
            class="login-button"
            :disabled="totpLoading"
            :class="{ loading: totpLoading }"
            @click="handleTotpVerify"
          >
            <span v-if="!totpLoading">Verify</span>
            <span v-else class="loading-spinner"><span class="spinner"></span>Verifying...</span>
          </button>

          <button type="button" class="totp-alt-btn" @click="useRecovery = !useRecovery">
            {{ useRecovery ? 'Use authenticator code instead' : "Can't access your authenticator? Use a recovery code" }}
          </button>

          <button type="button" class="totp-back-btn" @click="backToLogin">
            &larr; Back to login
          </button>
        </div>
      </div>

      <!-- Login Form -->
      <div v-else class="form-section">
        <div class="form-header">
          <h2 class="form-title">Login</h2>
        </div>

        <form @submit.prevent="handleLogin" class="login-form">
          <div class="form-group">
            <label for="phone" class="form-label">Phone Number</label>
            <InternationalPhoneInput
              v-model="phoneData"
              placeholder="01112223334"
              :has-error="!!errors.phone"
            />
            <span v-if="errors.phone" class="error-message">{{ errors.phone }}</span>
          </div>

          <div class="form-group">
            <label for="password" class="form-label">Password</label>
            <div class="input-wrapper">
              <svg class="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <circle cx="12" cy="7" r="4"/>
                <path d="m8 11 0-4a4 4 0 0 1 8 0v4"/>
              </svg>
              <input
                id="password"
                v-model="loginForm.password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="Enter your password"
                class="form-input"
                :class="{ 'error': errors.password }"
                required
              />
              <button
                type="button"
                @click="togglePassword"
                class="toggle-password"
              >
                <svg v-if="!showPassword" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
                <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="m9.88 9.88a3 3 0 1 0 4.24 4.24"/>
                  <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 11 8 11 8a13.16 13.16 0 0 1-1.67 2.68"/>
                  <path d="M6.61 6.61A13.526 13.526 0 0 0 1 12s4 8 11 8a9.74 9.74 0 0 0 5.39-1.61"/>
                  <line x1="2" y1="2" x2="22" y2="22"/>
                </svg>
              </button>
            </div>
            <span v-if="errors.password" class="error-message">{{ errors.password }}</span>
          </div>

          <div class="form-options">
            <router-link to="/forgot-password" class="forgot-link">Forgot password?</router-link>
          </div>

          <TurnstileWidget
            ref="turnstile"
            :site-key="turnstileSiteKey"
            v-model:token="turnstileToken"
          />

          <button
            type="submit"
            class="login-button"
            :disabled="isLoading"
            :class="{ 'loading': isLoading }"
          >
            <span v-if="!isLoading">Sign In</span>
            <span v-else class="loading-spinner">
              <span class="spinner"></span>
              Signing in...
            </span>
          </button>

          <router-link to="/register" class="register-button">
            Register ⭐
          </router-link>

          <router-link to="/agent-login" class="agent-login-button">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
            </svg>
            Agent Login
          </router-link>

          <div v-if="successMessage" class="success-message">
            {{ successMessage }}
          </div>

          <div v-if="loginError" class="login-error">
            {{ loginError }}
          </div>
        </form>

        <div class="privacy-footer">
          <p class="privacy-text">
            By using Quali-D, you agree to our
            <a
              href="https://quali-d.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              class="privacy-link"
            >Privacy Policy</a>
          </p>
        </div>

      </div><!-- end v-else login form -->
    </div>

  </div>
</template>

<script>
import { authStore } from '@/store/auth'
import { AuthService } from '@/services/auth'
import { DEFAULT_COUNTRY_CODE } from '@/utils/countryCodes'
import InternationalPhoneInput from '@/components/InternationalPhoneInput.vue'
import TurnstileWidget from '@/components/TurnstileWidget.vue'
import { themeStore } from '@/store/theme'

export default {
  name: 'LoginView',
  components: {
    InternationalPhoneInput,
    TurnstileWidget
  },
  data () {
    return {
      phoneData: {
        countryCode: DEFAULT_COUNTRY_CODE,
        phone: ''
      },
      loginForm: {
        password: ''
      },
      errors: {},
      showPassword: false,
      isLoading: false,
      loginError: '',
      successMessage: '',
      themeStore,
      // TOTP
      totpStep: false,
      tempToken: '',
      totpCode: '',
      totpError: '',
      totpLoading: false,
      useRecovery: false,
      recoveryCode: '',
      // Turnstile
      turnstileToken: '',
      turnstileSiteKey: process.env.VUE_APP_TURNSTILE_SITE_KEY || '0x4AAAAAAC_1sD05pRRxvRCk',
      // Device info — computed in mounted(), passed with TOTP verify
      deviceFingerprint: '',
      deviceName: ''
    }
  },
  methods: {
    togglePassword () {
      this.showPassword = !this.showPassword
    },
    validateForm () {
      this.errors = {}
      let isValid = true

      if (!this.phoneData.phone.trim()) {
        this.errors.phone = 'Phone number is required'
        isValid = false
      } else if (!/^[0-9+\-\s()]+$/.test(this.phoneData.phone)) {
        this.errors.phone = 'Please enter a valid phone number'
        isValid = false
      }

      if (!this.loginForm.password.trim()) {
        this.errors.password = 'Password is required'
        isValid = false
      } else if (this.loginForm.password.length < 6) {
        this.errors.password = 'Password must be at least 6 characters'
        isValid = false
      }

      return isValid
    },
    async handleLogin () {
      this.loginError = ''
      if (!this.validateForm()) return

      if (!this.turnstileToken) {
        this.loginError = 'Please complete the security check.'
        return
      }

      this.isLoading = true
      try {
        const countryCode = this.phoneData.countryCode === '20' ? '2' : this.phoneData.countryCode
        const response = await AuthService.login({
          country_code: countryCode,
          phone: this.phoneData.phone,
          password: this.loginForm.password,
          cf_turnstile_response: this.turnstileToken
        })

        if (response.totp_required) {
          this.tempToken = response.temp_token
          this.totpStep = true
          return
        }

        this._finishLogin(response)
      } catch (error) {
        this.loginError = error.message || 'Login failed. Please try again.'
        this.$refs.turnstile?.reset()
      } finally {
        this.isLoading = false
      }
    },

    async handleTotpVerify () {
      this.totpError = ''
      const code = this.useRecovery ? this.recoveryCode : this.totpCode
      if (!code) return

      this.totpLoading = true
      try {
        const endpoint = this.useRecovery ? '/login/verify-recovery' : '/login/verify-totp'
        const res = await fetch(`https://api.quali-d.com${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            temp_token: this.tempToken,
            code,
            platform: 'web',
            device_fingerprint: this.deviceFingerprint,
            device_name: this.deviceName
          })
        })
        const data = await res.json()
        if (!data.success) throw new Error(data.message || 'Invalid code')

        // Build the user object exactly like AuthService.login() does
        data.user = {
          id: data.user_id,
          name: data.name,
          phone: data.phone,
          email: data.email,
          role: data.role_name,
          role_id: data.role_id,
          roleId: data.role_id,
          company: data.company_name,
          plan: data.plan_name,
          country_code: data.country_code
        }
        this._finishLogin(data)
      } catch (error) {
        this.totpError = error.message || 'Verification failed'
      } finally {
        this.totpLoading = false
      }
    },

    _finishLogin (response) {
      AuthService.setAuthData(response)
      authStore.setUser(response.user)
      authStore.subscriptionExpired = false
      authStore.subscriptionChecked = false
      authStore.loadCredits()
      this.$router.push('/')
    },

    backToLogin () {
      this.totpStep = false
      this.tempToken = ''
      this.totpCode = ''
      this.totpError = ''
      this.useRecovery = false
      this.recoveryCode = ''
    }
  },
  mounted () {
    // Pre-compute device info for TOTP verify (so it's ready if needed)
    this.deviceFingerprint = AuthService.generateDeviceFingerprint()
    this.deviceName = AuthService.getDeviceName()

    // Check for success message from forgot password flow
    const urlParams = new URLSearchParams(window.location.search)
    const message = urlParams.get('message')
    if (message) {
      this.successMessage = decodeURIComponent(message)
      // Clear the URL parameter
      const url = new URL(window.location)
      url.searchParams.delete('message')
      window.history.replaceState({}, document.title, url.pathname)
    }
  }
}
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  position: relative;
  overflow: hidden;
}

.login-form-container {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 48px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 480px;
  margin: 20px;
  position: relative;
  z-index: 10;
}

.logo-section {
  text-align: center;
  margin-bottom: 48px;
}

.logo-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 12px;
}

.logo {
  height: 48px;
  width: auto;
}

.brand-title {
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.brand-subtitle {
  color: #6b7280;
  font-size: 16px;
  margin: 0;
  font-weight: 500;
}

.form-section {
  width: 100%;
}

.form-header {
  text-align: center;
  margin-bottom: 32px;
}

.form-title {
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 8px;
}

.form-subtitle {
  color: #6b7280;
  font-size: 16px;
  margin: 0;
}

.login-form {
  width: 100%;
}

.form-group {
  margin-bottom: 24px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 16px;
  width: 18px;
  height: 18px;
  opacity: 0.6;
  z-index: 2;
  color: #6b7280;
}

.form-input {
  width: 100%;
  padding: 16px 16px 16px 52px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 16px;
  background: white;
  color: #1f2937;
  transition: all 0.2s;
  outline: none;
}

.form-input:focus {
  border-color: #3b82f6;
  background: white;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-input.error {
  border-color: #ef4444;
  background: #fef2f2;
}

.toggle-password {
  position: absolute;
  right: 16px;
  background: none;
  border: none;
  cursor: pointer;
  opacity: 0.6;
  padding: 4px;
  border-radius: 4px;
  transition: opacity 0.2s;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toggle-password:hover {
  opacity: 1;
}

.error-message {
  display: block;
  color: #ef4444;
  font-size: 14px;
  margin-top: 6px;
}

.form-options {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 32px;
}

.forgot-link {
  font-size: 14px;
  color: #3b82f6;
  text-decoration: none;
  font-weight: 500;
}

.forgot-link:hover {
  text-decoration: underline;
}

.login-button {
  width: 100%;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  border: none;
  padding: 16px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  overflow: hidden;
}

.login-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.3);
}

.login-button:disabled {
  opacity: 0.8;
  cursor: not-allowed;
  transform: none;
}

.register-button {
  display: block;
  width: 100%;
  text-align: center;
  padding: 16px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  color: #10b981;
  background: rgba(16, 185, 129, 0.1);
  border: 2px solid #10b981;
  margin-top: 16px;
  transition: all 0.2s;
}

.register-button:hover {
  background: #10b981;
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 8px 25px rgba(16, 185, 129, 0.3);
}

.agent-login-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  text-align: center;
  padding: 16px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  color: #6366f1;
  background: rgba(99, 102, 241, 0.1);
  border: 2px solid #6366f1;
  margin-top: 16px;
  transition: all 0.2s;
}

.agent-login-button:hover {
  background: #6366f1;
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 8px 25px rgba(99, 102, 241, 0.3);
}

.agent-login-button svg {
  width: 18px;
  height: 18px;
}

.loading-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.success-message {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #059669;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  margin-top: 16px;
  text-align: center;
}

.login-error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  margin-top: 16px;
  text-align: center;
}

.privacy-footer {
  text-align: center;
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
}

.privacy-text {
  color: #6b7280;
  font-size: 13px;
  margin: 0;
}

.privacy-link {
  color: #3b82f6;
  font-weight: 700;
  text-decoration: underline;
  font-size: 13px;
}

.privacy-link:hover {
  color: #1d4ed8;
}

.form-footer {
  text-align: center;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
}

.footer-text {
  color: #6b7280;
  font-size: 14px;
  margin: 0;
}

.signup-link {
  color: #3b82f6;
  text-decoration: none;
  font-weight: 600;
}

.signup-link:hover {
  text-decoration: underline;
}

.meta-partner-badge {
  text-align: center;
  margin-top: 12px;
}

.meta-partner-logo {
  display: block;
  height: 48px;
  width: auto;
  margin: 0 auto;
  opacity: 0.9;
}

.totp-form {
  width: 100%;
}

.totp-input {
  text-align: center;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: 8px;
}

.totp-alt-btn {
  display: block;
  width: 100%;
  background: none;
  border: none;
  color: #3b82f6;
  font-size: 14px;
  cursor: pointer;
  padding: 12px 0;
  text-align: center;
  text-decoration: underline;
}

.totp-back-btn {
  display: block;
  width: 100%;
  background: none;
  border: none;
  color: #6b7280;
  font-size: 14px;
  cursor: pointer;
  padding: 8px 0;
  text-align: center;
}

.totp-back-btn:hover {
  color: #374151;
}

.theme-toggle-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: 1px solid #e5e7eb;
  border-radius: 50%;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 17px;
  transition: all 0.2s;
}

.theme-toggle-btn:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
}

@media (max-width: 640px) {
  .login-form-container {
    padding: 32px 24px;
    margin: 16px;
  }

  .brand-title {
    font-size: 24px;
  }

  .form-title {
    font-size: 24px;
  }

  .form-options {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
}
</style>

<style>
/* Dark mode overrides for login page — unscoped so html.dark selector works */
html.dark .login-container {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
}

html.dark .login-form-container {
  background: rgba(15, 23, 42, 0.97);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

html.dark .form-title {
  color: #f1f5f9;
}

html.dark .form-subtitle {
  color: #94a3b8;
}

html.dark .form-label {
  color: #cbd5e1;
}

html.dark .form-input {
  background: #1e293b;
  border-color: #334155;
  color: #f1f5f9;
}

html.dark .form-input:focus {
  border-color: #3b82f6;
  background: #1e293b;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
}

html.dark .form-input.error {
  border-color: #ef4444;
  background: #2d1212;
}

html.dark .input-icon {
  color: #94a3b8;
}

html.dark .toggle-password {
  color: #94a3b8;
}

html.dark .toggle-password:hover {
  color: #f1f5f9;
  opacity: 1;
}

html.dark .forgot-link {
  color: #60a5fa;
}

html.dark .forgot-link:hover {
  color: #93c5fd;
}

html.dark .error-message {
  color: #fca5a5;
}

html.dark .login-error {
  background: #2d1212;
  border-color: #7f1d1d;
  color: #fca5a5;
}

html.dark .success-message {
  background: #0d2d20;
  border-color: #14532d;
  color: #4ade80;
}

html.dark .privacy-footer {
  border-top-color: #334155;
}

html.dark .privacy-text {
  color: #64748b;
}

html.dark .privacy-link {
  color: #60a5fa;
}

html.dark .privacy-link:hover {
  color: #93c5fd;
}

html.dark .totp-alt-btn {
  color: #60a5fa;
}

html.dark .totp-back-btn {
  color: #64748b;
}

html.dark .totp-back-btn:hover {
  color: #94a3b8;
}

html.dark .theme-toggle-btn {
  border-color: #334155;
  color: #94a3b8;
}

html.dark .theme-toggle-btn:hover {
  background: #1e293b;
  border-color: #475569;
}
</style>
